import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { 
  LayoutDashboard, 
  FolderOpen, 
  Users, 
  TrendingUp,
  Calendar,
  PoundSterling,
  Upload,
  Settings,
  Phone,
  Mail,
  MapPin,
  Clock,
  Plus,
  Edit,
  Trash2,
  Eye,
  ChevronRight,
  Search,
  Filter,
  Save,
  ExternalLink,
  CheckCircle,
  XCircle,
  Plug,
  TestTube,
  Archive,
  RotateCcw,
  Flame,
  Snowflake,
  Thermometer,
  X,
  Kanban,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

// Owner-specific panel components
import LeadDetailModal from "@/components/owner/LeadDetailModal";
import PricingPanel from "@/components/owner/PricingPanel";
import KanbanBoard from "@/components/owner/KanbanBoard";
import CRMMetrics from "@/components/owner/CRMMetrics";
import PortfolioManager from "@/components/owner/PortfolioManager";
import ReviewManager from "@/components/owner/ReviewManager";
import PaymentsPanel from "@/components/owner/PaymentsPanel";
import NotificationSettings from "@/components/owner/NotificationSettings";
import OperationsPanel from "@/components/owner/OperationsPanel";
import AnalyticsPanel from "@/components/owner/AnalyticsPanel";
import RemindersPanel from "@/components/owner/RemindersPanel";
import ReferralsPanel from "@/components/owner/ReferralsPanel";
import BankSettingsPanel from "@/components/owner/BankSettingsPanel";
import EmailSettingsPanel from "@/components/owner/EmailSettingsPanel";
import CalendarPlanner from "@/components/owner/CalendarPlanner";

// Dashboard components (refactored)
import { 
  DashboardView, 
  LeadManagement, 
  CalendarManager, 
  SettingsPanel, 
  TapTrackPanel 
} from "@/components/dashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerDashboard = ({ view = "dashboard" }) => {
  const { owner } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const [financeSettings, setFinanceSettings] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [temperatureFilter, setTemperatureFilter] = useState("");
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  const [tapTrackSettings, setTapTrackSettings] = useState({});
  const [tapTrackHistory, setTapTrackHistory] = useState([]);
  const [leadDetailOpen, setLeadDetailOpen] = useState(null);
  const [expandedStat, setExpandedStat] = useState(null); // For expandable dashboard cards
  const [calendarSettings, setCalendarSettings] = useState({
    monday: { enabled: true, start: "09:00", end: "17:00" },
    tuesday: { enabled: true, start: "09:00", end: "17:00" },
    wednesday: { enabled: true, start: "09:00", end: "17:00" },
    thursday: { enabled: true, start: "09:00", end: "17:00" },
    friday: { enabled: true, start: "09:00", end: "17:00" },
    saturday: { enabled: true, start: "09:00", end: "13:00" },
    sunday: { enabled: false, start: "09:00", end: "17:00" },
    slot_duration: 60
  });
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState("");
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [calendarTab, setCalendarTab] = useState("planner"); // Calendar view tab state

  const token = localStorage.getItem("owner_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (view === "dashboard") {
      fetchDashboardData();
    } else if (view === "leads") {
      fetchLeads();
    } else if (view === "projects") {
      fetchProjects();
    } else if (view === "pricing") {
      // PricingPanel handles its own data
      setLoading(false);
    } else if (view === "settings") {
      fetchSettings();
    } else if (view === "integrations" || view === "test") {
      fetchSettings();
    } else if (view === "taptrack") {
      fetchTapTrackSettings();
      fetchTapTrackHistory();
    } else if (view === "calendar") {
      fetchCalendarSettings();
    } else if (view === "pipeline") {
      // Pipeline view manages its own data
      setLoading(false);
    } else if (view === "portfolio" || view === "reviews" || view === "payments" || view === "notifications") {
      // These components manage their own data
      setLoading(false);
    } else if (view === "operations" || view === "analytics" || view === "reminders" || view === "referrals") {
      // Operations panels manage their own data
      setLoading(false);
    } else if (view === "bank-settings" || view === "email-settings") {
      // Settings panels manage their own data
      setLoading(false);
    }
  }, [view]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/owner/dashboard`, { headers });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      if (showArchived) params.append("archived", "true");
      if (temperatureFilter) params.append("temperature", temperatureFilter);
      
      const response = await axios.get(`${API}/owner/leads?${params}`, { headers });
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch leads when filters change
  useEffect(() => {
    if (view === "leads") {
      fetchLeads();
    }
  }, [statusFilter, searchTerm, showArchived, temperatureFilter]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/owner/pricing`, { headers });
      setPricing(response.data);
    } catch (error) {
      console.error("Error fetching pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const [siteRes, financeRes] = await Promise.all([
        axios.get(`${API}/owner/site-settings`, { headers }),
        axios.get(`${API}/owner/finance-settings`, { headers })
      ]);
      setSiteSettings(siteRes.data);
      setFinanceSettings(financeRes.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTapTrackSettings = async () => {
    try {
      const response = await axios.get(`${API}/owner/taptrack`, { headers });
      setTapTrackSettings(response.data);
    } catch (error) {
      console.error("Error fetching TapTrack settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTapTrackHistory = async () => {
    try {
      const response = await axios.get(`${API}/owner/taptrack/history`, { headers });
      setTapTrackHistory(response.data.exports || []);
    } catch (error) {
      console.error("Error fetching TapTrack history:", error);
    }
  };

  const saveTapTrackSettings = async () => {
    try {
      await axios.put(`${API}/owner/taptrack`, tapTrackSettings, { headers });
      toast.success("TapTrack settings saved");
    } catch (error) {
      toast.error("Failed to save TapTrack settings");
    }
  };

  const testTapTrackConnection = async () => {
    try {
      const response = await axios.post(`${API}/owner/taptrack/test`, {}, { headers });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to test TapTrack connection");
    }
  };

  const exportLeadToTapTrack = async (leadId) => {
    try {
      await axios.post(`${API}/owner/taptrack/export`, { lead_id: leadId }, { headers });
      toast.success("Lead exported to TapTrack");
      fetchTapTrackHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export lead");
    }
  };

  const exportAllQualifiedLeads = async () => {
    try {
      const response = await axios.post(`${API}/owner/taptrack/export-bulk`, { status_filter: "qualified" }, { headers });
      toast.success(`Exported ${response.data.exported_count} leads to TapTrack`);
      fetchTapTrackHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export leads");
    }
  };

  // Calendar Availability Functions
  const fetchCalendarSettings = async () => {
    try {
      setLoading(true);
      const [workingHoursRes, blockedRes] = await Promise.all([
        axios.get(`${API}/owner/calendar/working-hours`, { headers }),
        axios.get(`${API}/owner/calendar/blocked-dates`, { headers })
      ]);
      
      if (workingHoursRes.data) {
        setCalendarSettings(workingHoursRes.data);
      }
      setBlockedDates(blockedRes.data?.blocked_dates || []);
    } catch (error) {
      console.error("Error fetching calendar settings:", error);
      toast.error("Failed to load calendar settings");
    } finally {
      setLoading(false);
    }
  };

  const saveCalendarSettings = async () => {
    setSavingCalendar(true);
    try {
      await axios.put(`${API}/owner/calendar/working-hours`, calendarSettings, { headers });
      toast.success("Calendar availability saved");
    } catch (error) {
      toast.error("Failed to save calendar settings");
    } finally {
      setSavingCalendar(false);
    }
  };

  const addBlockedDate = async () => {
    if (!newBlockedDate) {
      toast.error("Please select a date to block");
      return;
    }
    try {
      await axios.post(`${API}/owner/calendar/blocked-dates`, {
        date: newBlockedDate,
        reason: "Owner blocked",
        all_day: true
      }, { headers });
      toast.success("Date blocked");
      setNewBlockedDate("");
      fetchCalendarSettings();
    } catch (error) {
      toast.error("Failed to block date");
    }
  };

  const removeBlockedDate = async (dateId) => {
    try {
      await axios.delete(`${API}/owner/calendar/blocked-dates/${dateId}`, { headers });
      toast.success("Date unblocked");
      fetchCalendarSettings();
    } catch (error) {
      toast.error("Failed to unblock date");
    }
  };

  const updateDaySettings = (day, field, value) => {
    setCalendarSettings(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const updateLeadStatus = async (leadId, newStatus, jobValue = null) => {
    try {
      await axios.patch(`${API}/owner/leads/${leadId}/status`, 
        { status: newStatus, job_value: jobValue },
        { headers }
      );
      toast.success("Lead status updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleSaveSettings = async (type) => {
    try {
      if (type === "site") {
        await axios.put(`${API}/owner/site-settings`, siteSettings, { headers });
      } else if (type === "finance") {
        await axios.put(`${API}/owner/finance-settings`, financeSettings, { headers });
      }
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleProjectUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      await axios.post(`${API}/owner/projects/upload`, formData, { headers });
      toast.success("Project uploaded successfully");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to upload project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Delete this project?")) return;
    
    try {
      await axios.delete(`${API}/owner/projects/${projectId}`, { headers });
      toast.success("Project deleted");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const statusColors = {
    new_enquiry: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    quote_booked: "bg-purple-100 text-purple-700",
    quote_sent: "bg-indigo-100 text-indigo-700",
    job_won: "bg-green-100 text-green-700",
    job_lost: "bg-red-100 text-red-700"
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown";
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Dashboard View
  if (view === "dashboard") {
    // Helper to render stat detail modal
    const StatDetailModal = ({ stat, onClose }) => {
      if (!stat) return null;
      
      const getDetailContent = () => {
        switch (stat) {
          case "enquiries":
            return {
              title: "New Enquiries This Month",
              count: dashboardData?.stats?.leads_this_month || 0,
              items: leads.filter(l => l.status === "new_enquiry").slice(0, 5),
              emptyText: "No new enquiries",
              link: "/owner-dashboard/leads",
              linkText: "View All Leads"
            };
          case "quotes":
            return {
              title: "Quotes Booked This Month",
              count: dashboardData?.stats?.quotes_booked || 0,
              items: leads.filter(l => l.status === "quote_booked").slice(0, 5),
              emptyText: "No quotes booked",
              link: "/owner-dashboard/leads",
              linkText: "View All Leads"
            };
          case "jobs":
            return {
              title: "Jobs Won This Month",
              count: dashboardData?.stats?.jobs_won || 0,
              items: leads.filter(l => l.status === "job_won").slice(0, 5),
              emptyText: "No jobs won yet",
              link: "/owner-dashboard/leads",
              linkText: "View All Leads"
            };
          case "revenue":
            return {
              title: "Revenue This Month",
              count: `£${(dashboardData?.stats?.revenue_this_month || 0).toLocaleString()}`,
              items: leads.filter(l => l.status === "job_won" && l.job_value).slice(0, 5),
              emptyText: "No revenue recorded",
              link: "/owner-dashboard/leads",
              linkText: "View All Jobs"
            };
          default:
            return null;
        }
      };
      
      const content = getDetailContent();
      if (!content) return null;
      
      return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{content.title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold text-primary mb-6">{content.count}</p>
              
              {content.items && content.items.length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {content.items.map((item, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{item.customer_name || item.name || "Unknown"}</p>
                          <p className="text-sm text-gray-500">{item.project_type || "General"}</p>
                        </div>
                        <p className="text-sm font-medium text-primary">
                          £{item.estimate_low || item.estimated_min || 0} - £{item.estimate_high || item.estimated_max || 0}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">{content.emptyText}</p>
              )}
              
              <a 
                href={content.link} 
                className="mt-4 block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {content.linkText}
                <ChevronRight className="inline w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="p-6" data-testid="owner-dashboard-main">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {owner?.name || "Owner"}!</h1>
          <p className="text-gray-600">Here's what's happening with your business</p>
        </div>

        {/* Stats Grid - Clickable Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button 
            onClick={() => setExpandedStat("enquiries")}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all text-left group"
            data-testid="stat-enquiries"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.leads_this_month || 0}</p>
                <p className="text-sm text-gray-500">New Enquiries</p>
              </div>
            </div>
            <p className="text-xs text-blue-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
          </button>

          <button 
            onClick={() => setExpandedStat("quotes")}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-purple-300 hover:shadow-md transition-all text-left group"
            data-testid="stat-quotes"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.quotes_booked || 0}</p>
                <p className="text-sm text-gray-500">Quotes Booked</p>
              </div>
            </div>
            <p className="text-xs text-purple-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
          </button>

          <button 
            onClick={() => setExpandedStat("jobs")}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-green-300 hover:shadow-md transition-all text-left group"
            data-testid="stat-jobs"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.jobs_won || 0}</p>
                <p className="text-sm text-gray-500">Jobs Won</p>
              </div>
            </div>
            <p className="text-xs text-green-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
          </button>

          <button 
            onClick={() => setExpandedStat("revenue")}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all text-left group"
            data-testid="stat-revenue"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <PoundSterling className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">£{(dashboardData?.stats?.revenue_this_month || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">Revenue</p>
              </div>
            </div>
            <p className="text-xs text-emerald-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
          </button>
        </div>

        {/* Stat Detail Modal */}
        {expandedStat && <StatDetailModal stat={expandedStat} onClose={() => setExpandedStat(null)} />}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <label className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:border-primary transition-colors">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleProjectUpload}
              className="hidden"
            />
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Upload Project Photos</p>
                <p className="text-sm text-gray-500">Add new project to gallery</p>
              </div>
            </div>
          </label>

          <a href="/owner-dashboard/pricing" className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-primary transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <PoundSterling className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Edit Pricing</p>
                <p className="text-sm text-gray-500">Update estimator prices</p>
              </div>
            </div>
          </a>

          <a href="/owner-dashboard/leads" className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-primary transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">View All Leads</p>
                <p className="text-sm text-gray-500">Manage enquiries</p>
              </div>
            </div>
          </a>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Quotes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Recent Quote Requests</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboardData?.recent_quotes?.slice(0, 5).map((quote, index) => (
                <div key={index} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{quote.name}</p>
                    <p className="text-sm text-gray-500">{quote.project_type} - {quote.postcode}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-primary">
                      £{quote.estimated_min?.toLocaleString()} - £{quote.estimated_max?.toLocaleString()}
                    </p>
                    <p className={`text-xs px-2 py-1 rounded-full inline-block ${statusColors[quote.status] || "bg-gray-100 text-gray-700"}`}>
                      {formatStatus(quote.status)}
                    </p>
                  </div>
                </div>
              ))}
              {(!dashboardData?.recent_quotes || dashboardData.recent_quotes.length === 0) && (
                <div className="p-6 text-center text-gray-500">No recent quotes</div>
              )}
            </div>
          </div>

          {/* Upcoming Appointments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Upcoming Appointments</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboardData?.upcoming_appointments?.slice(0, 5).map((apt, index) => (
                <div key={index} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{apt.name}</p>
                      <p className="text-sm text-gray-500">{apt.postcode}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{apt.booking_date}</p>
                    <p className="text-sm text-gray-500">{apt.booking_time}</p>
                  </div>
                </div>
              ))}
              {(!dashboardData?.upcoming_appointments || dashboardData.upcoming_appointments.length === 0) && (
                <div className="p-6 text-center text-gray-500">No upcoming appointments</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Leads View
  if (view === "leads") {
    const temperatureColors = {
      hot: { bg: "bg-red-100", text: "text-red-700", icon: Flame },
      warm: { bg: "bg-orange-100", text: "text-orange-700", icon: Thermometer },
      cold: { bg: "bg-blue-100", text: "text-blue-700", icon: Snowflake }
    };

    return (
      <div className="p-6" data-testid="owner-leads-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Lead Management</h1>
          <p className="text-gray-600">Track and manage your enquiries</p>
        </div>

        {/* Archive Toggle Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={!showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(false)}
          >
            <Users className="w-4 h-4 mr-2" />
            Active Leads
          </Button>
          <Button
            variant={showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(true)}
          >
            <Archive className="w-4 h-4 mr-2" />
            Archived
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by name, phone, or postcode..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-4 py-2 text-gray-700"
            >
              <option value="">All Status</option>
              <option value="new_enquiry">New Enquiry</option>
              <option value="contacted">Contacted</option>
              <option value="quote_booked">Quote Booked</option>
              <option value="quote_sent">Quote Sent</option>
              <option value="job_won">Job Won</option>
              <option value="job_lost">Job Lost</option>
            </select>
            <select
              value={temperatureFilter}
              onChange={(e) => setTemperatureFilter(e.target.value)}
              className="border rounded-lg px-4 py-2 text-gray-700"
            >
              <option value="">All Temperatures</option>
              <option value="hot">🔥 Hot</option>
              <option value="warm">🌡️ Warm</option>
              <option value="cold">❄️ Cold</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Customer</th>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Project</th>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Estimate</th>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Status</th>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Temp</th>
                  <th className="text-left px-6 py-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((lead) => {
                  const tempInfo = lead.temperature ? temperatureColors[lead.temperature] : null;
                  const TempIcon = tempInfo?.icon;
                  
                  return (
                    <tr 
                      key={lead.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setLeadDetailOpen(lead)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {tempInfo && (
                            <div className={`w-2 h-2 rounded-full ${lead.temperature === "hot" ? "bg-red-500" : lead.temperature === "warm" ? "bg-orange-500" : "bg-blue-500"}`} />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{lead.customer_name || lead.name || "Unknown"}</p>
                            <p className="text-sm text-gray-500">{lead.phone || lead.email}</p>
                            <p className="text-xs text-gray-400">{lead.postcode}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-900 capitalize">{lead.project_type?.replace(/_/g, " ") || lead.source}</p>
                        <p className="text-xs text-gray-500">{new Date(lead.created_at).toLocaleDateString()}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-primary">
                          £{(lead.estimate_low || lead.estimated_min || 0).toLocaleString()} - £{(lead.estimate_high || lead.estimated_max || 0).toLocaleString()}
                        </p>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={lead.status || "new_enquiry"}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`text-sm px-3 py-1 rounded-full border-0 ${statusColors[lead.status] || "bg-gray-100 text-gray-700"}`}
                        >
                          <option value="new_enquiry">New Enquiry</option>
                          <option value="contacted">Contacted</option>
                          <option value="quote_booked">Quote Booked</option>
                          <option value="quote_sent">Quote Sent</option>
                          <option value="job_won">Job Won</option>
                          <option value="job_lost">Job Lost</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        {tempInfo ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${tempInfo.bg} ${tempInfo.text}`}>
                            <TempIcon className="w-3 h-3" />
                            {lead.temperature}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLeadDetailOpen(lead)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="p-2 rounded-lg hover:bg-gray-100">
                              <Phone className="w-4 h-4 text-gray-600" />
                            </a>
                          )}
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="p-2 rounded-lg hover:bg-gray-100">
                              <Mail className="w-4 h-4 text-gray-600" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {leads.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              {showArchived ? "No archived leads" : "No active leads found"}
            </div>
          )}
        </div>

        {/* Lead Detail Modal */}
        {leadDetailOpen && (
          <LeadDetailModal
            lead={leadDetailOpen}
            onClose={() => setLeadDetailOpen(null)}
            onUpdate={() => {
              fetchLeads();
              setLeadDetailOpen(null);
            }}
            token={token}
          />
        )}
      </div>
    );
  }

  // Projects View
  if (view === "projects") {
    return (
      <div className="p-6" data-testid="owner-projects-view">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-gray-600">Manage your project gallery</p>
          </div>
          <label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleProjectUpload}
              className="hidden"
            />
            <Button className="cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              Upload Project
            </Button>
          </label>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="aspect-video bg-gray-100">
                {project.images?.[0] && (
                  <img 
                    src={project.images[0]} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{project.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{project.location}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${project.featured ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                    {project.featured ? "Featured" : project.category}
                  </span>
                  <div className="flex gap-2">
                    <a href={`/projects/${project.id}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-gray-100">
                      <Eye className="w-4 h-4 text-gray-600" />
                    </a>
                    <button onClick={() => handleDeleteProject(project.id)} className="p-2 rounded-lg hover:bg-red-50">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No projects yet. Upload your first project!</p>
          </div>
        )}
      </div>
    );
  }

  // Pricing View
  if (view === "pricing") {
    return <PricingPanel token={token} />;
  }

  // Settings View - Business Settings
  if (view === "settings") {
    return (
      <div className="p-6" data-testid="owner-settings-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
          <p className="text-gray-600">Configure your company information and website settings</p>
        </div>

        {/* Company Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={siteSettings.company_name || "DC Tiling & Bathrooms"}
                onChange={(e) => setSiteSettings({ ...siteSettings, company_name: e.target.value })}
                placeholder="e.g. DC Tiling & Bathrooms"
                className="mt-1"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label>Service Area</Label>
              <Input
                value={siteSettings.service_area || ""}
                onChange={(e) => setSiteSettings({ ...siteSettings, service_area: e.target.value })}
                placeholder="e.g. Greater Manchester"
                className="mt-1"
                data-testid="service-area-input"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={siteSettings.contact_phone || ""}
                onChange={(e) => setSiteSettings({ ...siteSettings, contact_phone: e.target.value })}
                placeholder="e.g. 07547 393 455"
                className="mt-1"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={siteSettings.contact_email || ""}
                onChange={(e) => setSiteSettings({ ...siteSettings, contact_email: e.target.value })}
                placeholder="e.g. info@dctiling.co.uk"
                className="mt-1"
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <Button className="mt-4" onClick={() => handleSaveSettings("site")} data-testid="save-business-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Business Settings
          </Button>
        </div>

        {/* Finance Provider Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Finance Provider</h2>
          <div>
            <Label>Finance Application Link</Label>
            <p className="text-sm text-gray-500 mb-2">
              Enter the URL for your finance provider's application page. This link will appear on the Finance page.
            </p>
            <Input
              value={siteSettings.finance_provider_link || financeSettings.finance_url || ""}
              onChange={(e) => {
                setSiteSettings({ ...siteSettings, finance_provider_link: e.target.value });
                setFinanceSettings({ ...financeSettings, finance_url: e.target.value });
              }}
              placeholder="e.g. https://kanda.co.uk/apply/your-business"
              className="mt-1"
              data-testid="finance-link-input"
            />
            {(siteSettings.finance_provider_link || financeSettings.finance_url) && (
              <a 
                href={siteSettings.finance_provider_link || financeSettings.finance_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1"
              >
                Test link <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <Button className="mt-4" onClick={() => handleSaveSettings("finance")} data-testid="save-finance-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Finance Settings
          </Button>
        </div>
      </div>
    );
  }

  // Integrations View
  if (view === "integrations") {
    return (
      <div className="p-6" data-testid="owner-integrations-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Integrations & Notifications</h1>
          <p className="text-gray-600">Connect external services and configure notifications</p>
        </div>

        {/* Integration Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Integration Status</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <div>
                <p className="font-medium text-gray-900">Google Calendar</p>
                <p className="text-xs text-gray-500">Not Connected</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <div>
                <p className="font-medium text-gray-900">Google Business</p>
                <p className="text-xs text-gray-500">Not Connected</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${siteSettings.contact_email ? "bg-green-500" : "bg-yellow-500"}`} />
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-xs text-gray-500">{siteSettings.contact_email ? "Active" : "Not Configured"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${financeSettings.finance_url ? "bg-green-500" : "bg-yellow-500"}`} />
              <div>
                <p className="font-medium text-gray-900">Finance Link</p>
                <p className="text-xs text-gray-500">{financeSettings.finance_url ? "Active" : "Not Configured"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Google Calendar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Google Calendar Integration</h2>
          <p className="text-gray-600 mb-4">Connect your Google account to manage quote appointments.</p>
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Setup Required:</strong> To enable Google Calendar integration, you need to provide Google API credentials (GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET) as environment variables.
            </p>
          </div>
          <Button variant="outline" disabled>
            Connect Google Account
          </Button>
          
          <div className="mt-6 border-t pt-6">
            <h3 className="font-medium text-gray-900 mb-3">Calendar Event Settings</h3>
            <div className="space-y-2">
              {["Include customer name", "Include phone number", "Include email address", "Include postcode", "Include project type", "Include estimate range"].map((item) => (
                <label key={item} className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded" />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Email Notifications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Notification Settings</h2>
          <div className="mb-4">
            <Label>Lead Notification Email Address</Label>
            <Input
              value={siteSettings.contact_email || ""}
              onChange={(e) => setSiteSettings({ ...siteSettings, contact_email: e.target.value })}
              placeholder="info@dctiling.co.uk"
              className="mt-1"
            />
          </div>
          
          <div className="mb-4">
            <Label>Email Subject Format</Label>
            <Input
              defaultValue="New Quote Request – DC Tiling & Bathrooms"
              placeholder="Email subject template"
              className="mt-1"
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-medium text-gray-900 mb-3">Email Content Options</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {["Include customer name", "Include phone number", "Include email", "Include postcode", "Include project type", "Include estimate range", "Include project scope", "Include uploaded photos", "Include booking time"].map((item) => (
                <label key={item} className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded" />
                  <span className="text-sm text-gray-700">{item}</span>
                </label>
              ))}
            </div>
          </div>
          
          <Button className="mt-4" onClick={() => handleSaveSettings("site")}>
            <Save className="w-4 h-4 mr-2" />
            Save Email Settings
          </Button>
        </div>

        {/* Lead Notification Options */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lead Notification Options</h2>
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" defaultChecked className="rounded" />
              <div>
                <p className="font-medium text-gray-900">Send email notification for new leads</p>
                <p className="text-xs text-gray-500">Receive an email when a new quote request is submitted</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" className="rounded" />
              <div>
                <p className="font-medium text-gray-900">Send SMS notification (requires Twilio)</p>
                <p className="text-xs text-gray-500">Receive SMS alerts for new leads</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" defaultChecked className="rounded" />
              <div>
                <p className="font-medium text-gray-900">Add lead to Lead Management dashboard</p>
                <p className="text-xs text-gray-500">Automatically track all leads in CRM</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" className="rounded" />
              <div>
                <p className="font-medium text-gray-900">Create calendar event for booked quotes</p>
                <p className="text-xs text-gray-500">Requires Google Calendar connection</p>
              </div>
            </label>
          </div>
        </div>
      </div>
    );
  }

  // Test Integrations View
  if (view === "test") {
    const runTest = async (testType) => {
      setTesting(prev => ({ ...prev, [testType]: true }));
      
      try {
        const response = await axios.post(`${API}/owner/test/${testType}`, {}, { headers });
        const result = response.data;
        
        setTestResults(prev => ({ ...prev, [testType]: result }));
        
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        const errorResult = { success: false, message: error.response?.data?.detail || "Test failed" };
        setTestResults(prev => ({ ...prev, [testType]: errorResult }));
        toast.error(errorResult.message);
      } finally {
        setTesting(prev => ({ ...prev, [testType]: false }));
      }
    };

    const runAllTests = async () => {
      setTesting(prev => ({ ...prev, all: true }));
      try {
        const response = await axios.post(`${API}/owner/test/run-all`, {}, { headers });
        const data = response.data;
        
        // Convert results array to object keyed by test_type
        const resultsObj = {};
        data.results.forEach(r => {
          resultsObj[r.test_type] = r;
        });
        setTestResults(resultsObj);
        
        toast.success(`Tests complete: ${data.summary.passed}/${data.summary.total} passed`);
      } catch (error) {
        toast.error("Failed to run tests");
      } finally {
        setTesting(prev => ({ ...prev, all: false }));
      }
    };

    return (
      <div className="p-6" data-testid="owner-test-view">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Integrations</h1>
            <p className="text-gray-600">Verify all connected services are working correctly</p>
          </div>
          <Button onClick={runAllTests} disabled={testing.all}>
            <TestTube className="w-4 h-4 mr-2" />
            {testing.all ? "Running..." : "Run All Tests"}
          </Button>
        </div>

        {/* System Diagnostic */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Diagnostic</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: "database", label: "Database Connection", desc: "Test MongoDB connection" },
              { id: "email", label: "Email Notifications", desc: "Test email configuration" },
              { id: "calendar", label: "Google Calendar", desc: "Test calendar integration" },
              { id: "sms", label: "SMS Notifications", desc: "Test Twilio SMS" },
              { id: "finance", label: "Finance Link", desc: "Test finance application URL" },
              { id: "lead", label: "Create Test Lead", desc: "Add a test lead to CRM" }
            ].map((test) => (
              <div key={test.id} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{test.label}</p>
                    <p className="text-xs text-gray-500">{test.desc}</p>
                  </div>
                  {testResults[test.id] && (
                    <div className={`w-3 h-3 rounded-full ${testResults[test.id].success ? "bg-green-500" : "bg-red-500"}`} />
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => runTest(test.id)}
                  disabled={testing[test.id]}
                  className="w-full"
                >
                  {testing[test.id] ? "Testing..." : "Run Test"}
                </Button>
                {testResults[test.id] && (
                  <p className={`text-xs mt-2 ${testResults[test.id].success ? "text-green-600" : "text-red-600"}`}>
                    {testResults[test.id].message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Test Results Summary */}
        {Object.keys(testResults).length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Results Summary</h2>
            <div className="space-y-2">
              {Object.entries(testResults).map(([key, result]) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 capitalize">{key.replace("_", " ")}</p>
                    <p className="text-xs text-gray-500">{result.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Configuration Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration Checklist</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-gray-900">Database</p>
                <p className="text-xs text-gray-500">MongoDB connected and operational</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <XCircle className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="font-medium text-gray-900">Google Calendar</p>
                <p className="text-xs text-gray-500">Requires GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <XCircle className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="font-medium text-gray-900">SMS Notifications</p>
                <p className="text-xs text-gray-500">Requires TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TapTrack Integration View
  if (view === "taptrack") {
    return (
      <div className="p-6" data-testid="owner-taptrack-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">TapTrack Integration</h1>
          <p className="text-gray-600">Export qualified leads to your TapTrack operational system</p>
        </div>

        {/* TapTrack Connection Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">TapTrack API Configuration</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>API URL</Label>
              <Input
                value={tapTrackSettings.api_url || ""}
                onChange={(e) => setTapTrackSettings({ ...tapTrackSettings, api_url: e.target.value })}
                placeholder="https://api.taptrack.co.uk/v1"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Workspace ID</Label>
              <Input
                value={tapTrackSettings.workspace_id || ""}
                onChange={(e) => setTapTrackSettings({ ...tapTrackSettings, workspace_id: e.target.value })}
                placeholder="ws_xxxxxxxx"
                className="mt-1"
              />
            </div>
          </div>
          <div className="mb-4">
            <Label>API Key</Label>
            <Input
              type="password"
              value={tapTrackSettings.api_key || ""}
              onChange={(e) => setTapTrackSettings({ ...tapTrackSettings, api_key: e.target.value })}
              placeholder="Enter your TapTrack API key"
              className="mt-1"
            />
            {tapTrackSettings.has_api_key && (
              <p className="text-xs text-gray-500 mt-1">
                Current key: {tapTrackSettings.api_key_masked || "****"}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={tapTrackSettings.enabled || false}
                onCheckedChange={(checked) => setTapTrackSettings({ ...tapTrackSettings, enabled: checked })}
              />
              <span className="text-sm text-gray-700">Enable TapTrack Integration</span>
            </label>
          </div>
          <div className="flex gap-3">
            <Button onClick={saveTapTrackSettings}>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
            <Button variant="outline" onClick={testTapTrackConnection}>
              <Plug className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
          </div>
        </div>

        {/* Export Options */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Leads</h2>
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Export Fields:</strong> Customer name, phone, email, postcode, project type, estimate value range, appointment date, and notes.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={exportAllQualifiedLeads}
              disabled={!tapTrackSettings.enabled}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Export All Qualified Leads
            </Button>
          </div>
          {!tapTrackSettings.enabled && (
            <p className="text-xs text-gray-500 mt-2">
              Enable TapTrack integration above to export leads
            </p>
          )}
        </div>

        {/* Export History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export History</h2>
          {tapTrackHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No exports yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Lead ID</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Customer</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Exported At</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tapTrackHistory.slice(0, 20).map((exp) => (
                    <tr key={exp.id} className="border-b">
                      <td className="py-2 px-3 text-sm text-gray-700">{exp.lead_id?.slice(0, 8)}...</td>
                      <td className="py-2 px-3 text-sm text-gray-700">{exp.export_data?.customer_name || "-"}</td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {new Date(exp.exported_at).toLocaleDateString()} {new Date(exp.exported_at).toLocaleTimeString()}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          exp.status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {exp.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Calendar Availability View
  if (view === "calendar") {
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const timeOptions = [];
    for (let h = 6; h <= 21; h++) {
      timeOptions.push(`${h.toString().padStart(2, "0")}:00`);
      timeOptions.push(`${h.toString().padStart(2, "0")}:30`);
    }

    return (
      <div className="p-8" data-testid="calendar-availability-view">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Calendar & Availability</h1>
          <p className="text-gray-500">Plan your schedule and manage booking availability</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={calendarTab === "planner" ? "default" : "outline"}
            onClick={() => setCalendarTab("planner")}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Calendar Planner
          </Button>
          <Button
            variant={calendarTab === "weekly" ? "default" : "outline"}
            onClick={() => setCalendarTab("weekly")}
          >
            <Clock className="w-4 h-4 mr-2" />
            Weekly Defaults
          </Button>
        </div>

        {/* Calendar Planner Tab */}
        {calendarTab === "planner" && (
          <CalendarPlanner token={localStorage.getItem("owner_token")} />
        )}

        {/* Weekly Defaults Tab */}
        {calendarTab === "weekly" && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Working Hours */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Default Working Hours
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Set default availability for each day of the week. You can override these for specific dates in the Calendar Planner.
              </p>

              <div className="space-y-4">
                {days.map((day) => (
                  <div key={day} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
                    <div className="w-24">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={calendarSettings[day]?.enabled}
                          onCheckedChange={(checked) => updateDaySettings(day, "enabled", checked)}
                          data-testid={`day-${day}-enabled`}
                        />
                        <span className="text-sm font-medium capitalize">{day}</span>
                      </label>
                    </div>
                    
                    {calendarSettings[day]?.enabled && (
                      <div className="flex items-center gap-2 flex-1">
                        <select
                          value={calendarSettings[day]?.start || "09:00"}
                          onChange={(e) => updateDaySettings(day, "start", e.target.value)}
                          className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                          data-testid={`day-${day}-start`}
                        >
                          {timeOptions.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                        <span className="text-gray-500">to</span>
                        <select
                          value={calendarSettings[day]?.end || "17:00"}
                          onChange={(e) => updateDaySettings(day, "end", e.target.value)}
                          className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                          data-testid={`day-${day}-end`}
                        >
                          {timeOptions.map((time) => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {!calendarSettings[day]?.enabled && (
                      <span className="text-sm text-gray-400 italic">Closed</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <Label className="text-sm">Appointment Duration (minutes)</Label>
                <select
                  value={calendarSettings.slot_duration || 60}
                  onChange={(e) => setCalendarSettings(prev => ({ ...prev, slot_duration: parseInt(e.target.value) }))}
                  className="mt-2 h-9 px-3 rounded-md border border-input bg-background text-sm w-32"
                  data-testid="slot-duration"
                >
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <Button 
                className="w-full mt-6" 
                onClick={saveCalendarSettings}
                disabled={savingCalendar}
                data-testid="save-calendar-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingCalendar ? "Saving..." : "Save Default Hours"}
              </Button>
            </div>

            {/* Quick Block Dates */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-500" />
                Quick Block Dates
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Block specific dates when you're unavailable. For more control, use the Calendar Planner.
              </p>

              <div className="flex gap-2 mb-4">
                <Input
                  type="date"
                  value={newBlockedDate}
                  onChange={(e) => setNewBlockedDate(e.target.value)}
                  className="flex-1"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="new-blocked-date-input"
                />
                <Button onClick={addBlockedDate} data-testid="add-blocked-date-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Block
                </Button>
              </div>

              {blockedDates.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {blockedDates.map((blocked) => (
                    <div 
                      key={blocked.id || blocked.date} 
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {new Date(blocked.date).toLocaleDateString('en-GB', { 
                            weekday: 'short', 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </p>
                        {blocked.reason && (
                          <p className="text-xs text-gray-500">{blocked.reason}</p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeBlockedDate(blocked.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-100"
                        data-testid={`remove-blocked-${blocked.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No blocked dates</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">How it works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Calendar Planner:</strong> Set custom availability for specific dates, override weekly defaults</li>
            <li>• <strong>Weekly Defaults:</strong> Set your standard working hours for each day of the week</li>
            <li>• Custom date settings always override weekly defaults</li>
            <li>• Changes take effect immediately on the public booking page</li>
          </ul>
        </div>
      </div>
    );
  }

  // Pipeline / Kanban View
  if (view === "pipeline") {
    return (
      <div className="p-6" data-testid="pipeline-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Sales Pipeline</h1>
          <p className="text-gray-500">Drag and drop leads between stages to update their status</p>
        </div>

        {/* CRM Metrics */}
        <div className="mb-6">
          <CRMMetrics token={localStorage.getItem("owner_token")} />
        </div>

        {/* Kanban Board */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <KanbanBoard 
            token={localStorage.getItem("owner_token")} 
            onViewLead={(lead) => setLeadDetailOpen(lead)}
          />
        </div>

        {/* Lead Detail Modal */}
        {leadDetailOpen && (
          <LeadDetailModal
            lead={leadDetailOpen}
            onClose={() => setLeadDetailOpen(null)}
            onUpdate={() => {
              setLeadDetailOpen(null);
              // Refresh will happen via KanbanBoard's internal state
            }}
            token={localStorage.getItem("owner_token")}
          />
        )}
      </div>
    );
  }

  // Portfolio Manager View
  if (view === "portfolio") {
    return (
      <div className="p-6" data-testid="portfolio-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Portfolio Manager</h1>
          <p className="text-gray-500">Manage your project portfolio displayed on the website</p>
        </div>
        <PortfolioManager token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  // Review Manager View
  if (view === "reviews") {
    return (
      <div className="p-6" data-testid="reviews-view">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Review Manager</h1>
          <p className="text-gray-500">Manage customer reviews displayed on the website</p>
        </div>
        <ReviewManager token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  // Payments View
  if (view === "payments") {
    return (
      <div className="p-6" data-testid="payments-view">
        <PaymentsPanel token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  // Notification Settings View
  if (view === "notifications") {
    return (
      <div className="p-6" data-testid="notifications-view">
        <NotificationSettings token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  // Operations Panel View
  if (view === "operations") {
    return <OperationsPanel token={localStorage.getItem("owner_token")} />;
  }

  // Analytics Dashboard View
  if (view === "analytics") {
    return <AnalyticsPanel token={localStorage.getItem("owner_token")} />;
  }

  // Reminders & Follow-ups View
  if (view === "reminders") {
    return <RemindersPanel token={localStorage.getItem("owner_token")} />;
  }

  // Referrals View
  if (view === "referrals") {
    return <ReferralsPanel token={localStorage.getItem("owner_token")} />;
  }

  // Bank Settings View
  if (view === "bank-settings") {
    return (
      <div className="p-6" data-testid="bank-settings-view">
        <BankSettingsPanel token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  // Email Settings View
  if (view === "email-settings") {
    return (
      <div className="p-6" data-testid="email-settings-view">
        <EmailSettingsPanel token={localStorage.getItem("owner_token")} />
      </div>
    );
  }

  return null;
};

export default OwnerDashboard;
